/* eslint-disable react/prop-types */
import React, { useEffect } from "react";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

const createItem = () => ({
  bookingDate: "",
  documentNumber: "",
  documentDate: "",
  description: "",
  correspondingAccount: "",
  exchangeRate: "",
  discountDeadline: "",
  debitIncurredFC: "",
  debitIncurredVND: "",
  creditIncurredFC: "",
  creditIncurredVND: "",
  debitBalanceFC: "",
  debitBalanceVND: "",
  creditBalanceFC: "",
  creditBalanceVND: "",
});

export function SubsidiaryLedgerFCPaymentInfo({ formData }) {
  const { values, setFieldValue, containView } = formData;
  const { 
    details = [], 
    openingBalance = { debitFC: 0, debitVND: 0, creditFC: 0, creditVND: 0 } 
  } = values;

  const addItem = () => {
    setFieldValue("details", [...details, createItem()]);
  };

  const removeItem = (index) => {
    if (details.length <= 1) return;
    setFieldValue("details", details.filter((_, i) => i !== index));
  };

  const handleChangeItem = (index, field, val) => {
    const newDetails = [...details];
    newDetails[index][field] = val;
    setFieldValue("details", newDetails);
  };

  const num = (v) => (v && v !== "" && !isNaN(v) ? parseFloat(v) : 0);

  // Auto-calculation logic for Balances (Both Foreign Currency & VND)
  useEffect(() => {
    let currentDebitFC = num(openingBalance.debitFC);
    let currentCreditFC = num(openingBalance.creditFC);
    let currentDebitVND = num(openingBalance.debitVND);
    let currentCreditVND = num(openingBalance.creditVND);

    let totalDebitIncFC = 0;
    let totalCreditIncFC = 0;
    let totalDebitIncVND = 0;
    let totalCreditIncVND = 0;

    const newDetails = details.map((item) => {
      // FC (Ngoại tệ) Incurred
      const dIncFC = num(item.debitIncurredFC);
      const cIncFC = num(item.creditIncurredFC);
      totalDebitIncFC += dIncFC;
      totalCreditIncFC += cIncFC;

      // VND (Quy ra VNĐ) Incurred
      const dIncVND = num(item.debitIncurredVND);
      const cIncVND = num(item.creditIncurredVND);
      totalDebitIncVND += dIncVND;
      totalCreditIncVND += cIncVND;

      // Calculate FC Balance
      const netChangeFC = dIncFC - cIncFC;
      const initialNetFC = currentDebitFC - currentCreditFC;
      const finalNetFC = initialNetFC + netChangeFC;
      const dBalFC = finalNetFC > 0 ? finalNetFC : 0;
      const cBalFC = finalNetFC < 0 ? Math.abs(finalNetFC) : 0;

      // Calculate VND Balance
      const netChangeVND = dIncVND - cIncVND;
      const initialNetVND = currentDebitVND - currentCreditVND;
      const finalNetVND = initialNetVND + netChangeVND;
      const dBalVND = finalNetVND > 0 ? finalNetVND : 0;
      const cBalVND = finalNetVND < 0 ? Math.abs(finalNetVND) : 0;

      // Update current for next row
      currentDebitFC = dBalFC;
      currentCreditFC = cBalFC;
      currentDebitVND = dBalVND;
      currentCreditVND = cBalVND;

      return {
        ...item,
        debitBalanceFC: dBalFC,
        creditBalanceFC: cBalFC,
        debitBalanceVND: dBalVND,
        creditBalanceVND: cBalVND,
      };
    });

    // Final Closing Balance FC
    const openingNetFC = num(openingBalance.debitFC) - num(openingBalance.creditFC);
    const totalNetFC = totalDebitIncFC - totalCreditIncFC;
    const finalClosingNetFC = openingNetFC + totalNetFC;
    const closingDebitFC = finalClosingNetFC > 0 ? finalClosingNetFC : 0;
    const closingCreditFC = finalClosingNetFC < 0 ? Math.abs(finalClosingNetFC) : 0;

    // Final Closing Balance VND
    const openingNetVND = num(openingBalance.debitVND) - num(openingBalance.creditVND);
    const totalNetVND = totalDebitIncVND - totalCreditIncVND;
    const finalClosingNetVND = openingNetVND + totalNetVND;
    const closingDebitVND = finalClosingNetVND > 0 ? finalClosingNetVND : 0;
    const closingCreditVND = finalClosingNetVND < 0 ? Math.abs(finalClosingNetVND) : 0;

    const detailsChanged = JSON.stringify(newDetails) !== JSON.stringify(details);
    if (detailsChanged) {
      setFieldValue("details", newDetails);
      setFieldValue("totalIncurred", { 
        debitFC: totalDebitIncFC, creditFC: totalCreditIncFC,
        debitVND: totalDebitIncVND, creditVND: totalCreditIncVND
      });
      setFieldValue("closingBalance", { 
        debitFC: closingDebitFC, creditFC: closingCreditFC,
        debitVND: closingDebitVND, creditVND: closingCreditVND
      });
    }
  }, [
    JSON.stringify(details.map(i => [i.debitIncurredFC, i.creditIncurredFC, i.debitIncurredVND, i.creditIncurredVND])), 
    openingBalance.debitFC, openingBalance.creditFC, openingBalance.debitVND, openingBalance.creditVND
  ]);

  const s = {
    container: { fontFamily: '"Times New Roman", Times, serif', color: "#000", padding: "20px", backgroundColor: "#fff", fontSize: "14px" },
    headerTop: { display: "flex", justifyContent: "space-between", marginBottom: "15px" },
    titleBox: { textAlign: "center", margin: "10px 0" },
    inputDotted: { border: "none", borderBottom: "1px dotted #000", outline: "none", fontSize: "14px", fontFamily: "inherit", backgroundColor: "transparent", color: "#000", fontWeight: "bold" },
    inputDottedSmall: { border: "none", borderBottom: "1px dotted #000", outline: "none", width: "40px", fontSize: "14px", fontFamily: "inherit", textAlign: "center", backgroundColor: "transparent", color: "#000", fontWeight: "bold" },
    table: { width: "100%", borderCollapse: "collapse", fontSize: "13px", marginTop: "15px" },
    th: { border: "1px solid #000", padding: "8px 4px", textAlign: "center", fontWeight: "bold", verticalAlign: "middle", backgroundColor: "#fff" },
    td: { border: "1px solid #000", padding: 0, height: "35px", verticalAlign: "middle" },
    inputTable: { width: "100%", height: "100%", border: "none", outline: "none", padding: "0 4px", fontSize: "13px", fontFamily: "inherit", background: "transparent", color: "#000" },
    inputTableCenter: { width: "100%", height: "100%", border: "none", outline: "none", textAlign: "center", fontSize: "13px", fontFamily: "inherit", background: "transparent", color: "#000" },
    inputTableRight: { width: "100%", height: "100%", border: "none", outline: "none", textAlign: "right", padding: "0 4px", fontSize: "13px", fontFamily: "inherit", background: "transparent", color: "#000" },
    removeBtn: { border: "none", background: "transparent", color: "red", cursor: "pointer", fontWeight: "bold", fontSize: "16px" }
  };

  const fmt = (n) => (n && n !== 0 ? new Intl.NumberFormat("vi-VN").format(n) : "");

  return (
    <SoftBox style={s.container}>
      {/* HEADER */}
      <div style={s.headerTop}>
        <div style={{ width: "60%" }}>
          <div>Đơn vị: <input disabled={containView} style={{ ...s.inputDotted, width: "300px" }} value={values.unit} onChange={(e) => setFieldValue("unit", e.target.value)} /></div>
          <div>Địa chỉ: <input disabled={containView} style={{ ...s.inputDotted, width: "300px" }} value={values.address} onChange={(e) => setFieldValue("address", e.target.value)} /></div>
        </div>
        <div style={{ textAlign: "center", width: "40%" }}>
          <div style={{ fontWeight: "bold", fontSize: "15px" }}>Mẫu số S32-DN</div>
          <div style={{ fontStyle: "italic", fontSize: "13px" }}>(Kèm theo Thông tư số 99/2025/TT-BTC <br /> ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</div>
        </div>
      </div>

      {/* TITLE */}
      <div style={s.titleBox}>
        <h2 style={{ margin: 0, textTransform: "uppercase", fontSize: "18px" }}>SỔ CHI TIẾT THANH TOÁN VỚI NGƯỜI MUA (NGƯỜI BÁN) BẰNG NGOẠI TỆ</h2>
        <div style={{ fontWeight: "bold", fontSize: "15px", marginTop: "5px" }}>(Dùng cho TK: 131, 331)</div>

        <div style={{ marginTop: 15, fontWeight: "bold" }}>
          Tài khoản: <input disabled={containView} style={{ ...s.inputDotted, width: "200px", textAlign: "center" }} value={values.accountNumber} onChange={(e) => setFieldValue("accountNumber", e.target.value)} />
        </div>
        <div style={{ marginTop: 5, fontWeight: "bold" }}>
          Đối tượng: <input disabled={containView} style={{ ...s.inputDotted, width: "200px", textAlign: "center" }} value={values.entityName} onChange={(e) => setFieldValue("entityName", e.target.value)} />
        </div>
        <div style={{ marginTop: 5, fontWeight: "bold" }}>
          Loại ngoại tệ: <input disabled={containView} style={{ ...s.inputDotted, width: "150px", textAlign: "center" }} value={values.currency} onChange={(e) => setFieldValue("currency", e.target.value)} />
        </div>
      </div>

      {/* TABLE */}
      <div style={{ overflowX: "auto" }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={{ ...s.th, width: "70px" }} rowSpan={3}>Ngày, tháng ghi sổ</th>
              <th style={s.th} colSpan={2}>Chứng từ</th>
              <th style={{ ...s.th, width: "200px" }} rowSpan={3}>Diễn giải</th>
              <th style={{ ...s.th, width: "50px" }} rowSpan={3}>TK đối ứng</th>
              <th style={{ ...s.th, width: "60px" }} rowSpan={3}>Tỷ giá hối đoái</th>
              <th style={{ ...s.th, width: "80px" }} rowSpan={3}>Thời hạn được chiết khấu</th>
              <th style={s.th} colSpan={4}>Số phát sinh</th>
              <th style={s.th} colSpan={4}>Số dư</th>
              {!containView && <th style={{ ...s.th, width: "30px" }} rowSpan={3}>#</th>}
            </tr>
            <tr>
              <th style={{ ...s.th, width: "60px" }} rowSpan={2}>Số hiệu</th>
              <th style={{ ...s.th, width: "70px" }} rowSpan={2}>Ngày, tháng</th>
              <th style={s.th} colSpan={2}>Nợ</th>
              <th style={s.th} colSpan={2}>Có</th>
              <th style={s.th} colSpan={2}>Nợ</th>
              <th style={s.th} colSpan={2}>Có</th>
            </tr>
            <tr>
              <th style={{ ...s.th, width: "80px" }}>Ngoại tệ</th>
              <th style={{ ...s.th, width: "80px" }}>Quy ra VNĐ</th>
              <th style={{ ...s.th, width: "80px" }}>Ngoại tệ</th>
              <th style={{ ...s.th, width: "80px" }}>Quy ra VNĐ</th>
              <th style={{ ...s.th, width: "80px" }}>Ngoại tệ</th>
              <th style={{ ...s.th, width: "80px" }}>Quy ra VNĐ</th>
              <th style={{ ...s.th, width: "80px" }}>Ngoại tệ</th>
              <th style={{ ...s.th, width: "80px" }}>Quy ra VNĐ</th>
            </tr>
            <tr style={{ backgroundColor: "#f0f0f0" }}>
              <th style={s.th}>A</th>
              <th style={s.th}>B</th>
              <th style={s.th}>C</th>
              <th style={s.th}>D</th>
              <th style={s.th}>E</th>
              <th style={s.th}>1</th>
              <th style={s.th}>2</th>
              <th style={s.th}>3</th>
              <th style={s.th}>4</th>
              <th style={s.th}>5</th>
              <th style={s.th}>6</th>
              <th style={s.th}>7</th>
              <th style={s.th}>8</th>
              <th style={s.th}>9</th>
              <th style={s.th}>10</th>
              {!containView && <th style={s.th}></th>}
            </tr>
          </thead>
          <tbody>
            {/* Opening Balance Row */}
            <tr style={{ fontWeight: "bold" }}>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={{ ...s.td, padding: "0 8px" }}>- Số dư đầu kỳ</td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={s.td}><input disabled={containView} type="number" style={s.inputTableRight} value={openingBalance.debitFC} onChange={(e) => setFieldValue("openingBalance.debitFC", e.target.value)} /></td>
              <td style={s.td}><input disabled={containView} type="number" style={s.inputTableRight} value={openingBalance.debitVND} onChange={(e) => setFieldValue("openingBalance.debitVND", e.target.value)} /></td>
              <td style={s.td}><input disabled={containView} type="number" style={s.inputTableRight} value={openingBalance.creditFC} onChange={(e) => setFieldValue("openingBalance.creditFC", e.target.value)} /></td>
              <td style={s.td}><input disabled={containView} type="number" style={s.inputTableRight} value={openingBalance.creditVND} onChange={(e) => setFieldValue("openingBalance.creditVND", e.target.value)} /></td>
              {!containView && <td style={s.td}></td>}
            </tr>

            {/* details Row */}
            {(details || []).map((item, index) => (
              <tr key={index}>
                <td style={s.td}><input disabled={containView} style={s.inputTableCenter} value={item.bookingDate} onChange={(e) => handleChangeItem(index, "bookingDate", e.target.value)} /></td>
                <td style={s.td}><input disabled={containView} style={s.inputTableCenter} value={item.documentNumber} onChange={(e) => handleChangeItem(index, "documentNumber", e.target.value)} /></td>
                <td style={s.td}><input disabled={containView} style={s.inputTableCenter} value={item.documentDate} onChange={(e) => handleChangeItem(index, "documentDate", e.target.value)} /></td>
                <td style={s.td}><input disabled={containView} style={s.inputTable} value={item.description} onChange={(e) => handleChangeItem(index, "description", e.target.value)} /></td>
                <td style={s.td}><input disabled={containView} style={s.inputTableCenter} value={item.correspondingAccount} onChange={(e) => handleChangeItem(index, "correspondingAccount", e.target.value)} /></td>
                <td style={s.td}><input disabled={containView} style={s.inputTableCenter} value={item.exchangeRate} onChange={(e) => handleChangeItem(index, "exchangeRate", e.target.value)} /></td>
                <td style={s.td}><input disabled={containView} style={s.inputTableCenter} value={item.discountDeadline} onChange={(e) => handleChangeItem(index, "discountDeadline", e.target.value)} /></td>
                
                {/* Số phát sinh */}
                <td style={s.td}><input disabled={containView} type="number" style={s.inputTableRight} value={item.debitIncurredFC} onChange={(e) => handleChangeItem(index, "debitIncurredFC", e.target.value)} /></td>
                <td style={s.td}><input disabled={containView} type="number" style={s.inputTableRight} value={item.debitIncurredVND} onChange={(e) => handleChangeItem(index, "debitIncurredVND", e.target.value)} /></td>
                <td style={s.td}><input disabled={containView} type="number" style={s.inputTableRight} value={item.creditIncurredFC} onChange={(e) => handleChangeItem(index, "creditIncurredFC", e.target.value)} /></td>
                <td style={s.td}><input disabled={containView} type="number" style={s.inputTableRight} value={item.creditIncurredVND} onChange={(e) => handleChangeItem(index, "creditIncurredVND", e.target.value)} /></td>
                
                {/* Số dư */}
                <td style={{ ...s.td, textAlign: "right", padding: "0 4px" }}>{fmt(item.debitBalanceFC)}</td>
                <td style={{ ...s.td, textAlign: "right", padding: "0 4px" }}>{fmt(item.debitBalanceVND)}</td>
                <td style={{ ...s.td, textAlign: "right", padding: "0 4px" }}>{fmt(item.creditBalanceFC)}</td>
                <td style={{ ...s.td, textAlign: "right", padding: "0 4px" }}>{fmt(item.creditBalanceVND)}</td>

                {!containView && (
                  <td style={{ ...s.td, textAlign: "center" }}>
                    <button type="button" style={s.removeBtn} onClick={() => removeItem(index)}>–</button>
                  </td>
                )}
              </tr>
            ))}

            {/* Total Incurred Row */}
            <tr style={{ backgroundColor: "#fdfdfd", fontWeight: "bold" }}>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={{ ...s.td, padding: "0 8px" }}>- Cộng số phát sinh</td>
              <td style={{ ...s.td, textAlign: "center" }}>x</td>
              <td style={{ ...s.td, textAlign: "center" }}>x</td>
              <td style={{ ...s.td, textAlign: "center" }}>x</td>
              <td style={{ ...s.td, textAlign: "right", padding: "0 4px" }}>{fmt(values?.totalIncurred?.debitFC)}</td>
              <td style={{ ...s.td, textAlign: "right", padding: "0 4px" }}>{fmt(values?.totalIncurred?.debitVND)}</td>
              <td style={{ ...s.td, textAlign: "right", padding: "0 4px" }}>{fmt(values?.totalIncurred?.creditFC)}</td>
              <td style={{ ...s.td, textAlign: "right", padding: "0 4px" }}>{fmt(values?.totalIncurred?.creditVND)}</td>
              <td style={{ ...s.td, textAlign: "center" }}>x</td>
              <td style={{ ...s.td, textAlign: "center" }}>x</td>
              <td style={{ ...s.td, textAlign: "center" }}>x</td>
              <td style={{ ...s.td, textAlign: "center" }}>x</td>
              {!containView && <td style={s.td}></td>}
            </tr>

            {/* Closing Balance Row */}
            <tr style={{ backgroundColor: "#fafafa", fontWeight: "bold" }}>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={{ ...s.td, padding: "0 8px" }}>- Số dư cuối kỳ</td>
              <td style={{ ...s.td, textAlign: "center" }}>x</td>
              <td style={{ ...s.td, textAlign: "center" }}>x</td>
              <td style={{ ...s.td, textAlign: "center" }}>x</td>
              <td style={{ ...s.td, textAlign: "center" }}>x</td>
              <td style={{ ...s.td, textAlign: "center" }}>x</td>
              <td style={{ ...s.td, textAlign: "center" }}>x</td>
              <td style={{ ...s.td, textAlign: "center" }}>x</td>
              <td style={{ ...s.td, textAlign: "right", padding: "0 4px" }}>{fmt(values?.closingBalance?.debitFC)}</td>
              <td style={{ ...s.td, textAlign: "right", padding: "0 4px" }}>{fmt(values?.closingBalance?.debitVND)}</td>
              <td style={{ ...s.td, textAlign: "right", padding: "0 4px" }}>{fmt(values?.closingBalance?.creditFC)}</td>
              <td style={{ ...s.td, textAlign: "right", padding: "0 4px" }}>{fmt(values?.closingBalance?.creditVND)}</td>
              {!containView && <td style={s.td}></td>}
            </tr>
          </tbody>
        </table>

        {!containView && (
          <SoftBox mt={2} textAlign="center">
            <SoftButton variant="outlined" color="dark" onClick={addItem}>+ Thêm dòng chi tiết</SoftButton>
          </SoftBox>
        )}
      </div>

      {/* FOOTER */}
      <div style={{ marginTop: "25px" }}>
        <div style={{ marginBottom: "5px" }}>- Sổ này có <input disabled={containView} style={s.inputDottedSmall} value={values.totalPages} onChange={(e) => setFieldValue("totalPages", e.target.value)} /> trang, đánh số từ trang 01 đến trang...</div>
        <div>- Ngày mở sổ: <input disabled={containView} style={{ ...s.inputDotted, width: "150px" }} value={values.openingDate} onChange={(e) => setFieldValue("openingDate", e.target.value)} /></div>
      </div>

      {/* SIGNATURES BLOCK */}
      {/* <div style={{ display: "flex", justifyContent: "space-between", marginTop: "30px", textAlign: "center" }}>
        <div style={{ width: "30%" }}>
          <b style={{ textTransform: "none" }}>Người ghi sổ</b><br/>
          <span style={{ fontStyle: "italic", fontSize: "13px" }}>(Ký, họ tên)</span>
          <SoftBox mt={8}>
            <input disabled={containView} style={{ ...s.inputDotted, width: "150px", textAlign: "center" }} value={values.ledgerClerk} onChange={(e) => setFieldValue("ledgerClerk", e.target.value)} />
          </SoftBox>
        </div>
        <div style={{ width: "30%" }}>
          <b style={{ textTransform: "none" }}>Kế toán trưởng</b><br/>
          <span style={{ fontStyle: "italic", fontSize: "13px" }}>(Ký, họ tên)</span>
          <SoftBox mt={8}>
            <input disabled={containView} style={{ ...s.inputDotted, width: "150px", textAlign: "center" }} value={values.chiefAccountant} onChange={(e) => setFieldValue("chiefAccountant", e.target.value)} />
          </SoftBox>
        </div>
        <div style={{ width: "40%" }}>
          <div style={{ fontStyle: "italic", fontSize: "14px", marginBottom: "5px" }}>
            Ngày <input disabled={containView} style={s.inputDottedSmall} value={values.signingDay} onChange={(e) => setFieldValue("signingDay", e.target.value)} /> tháng <input disabled={containView} style={s.inputDottedSmall} value={values.signingMonth} onChange={(e) => setFieldValue("signingMonth", e.target.value)} /> năm <input disabled={containView} style={{ ...s.inputDottedSmall, width: "50px" }} value={values.signingYear} onChange={(e) => setFieldValue("signingYear", e.target.value)} />
          </div>
          <b style={{ textTransform: "none" }}>Người đại diện theo pháp luật</b><br/>
          <span style={{ fontStyle: "italic", fontSize: "13px" }}>(Ký, họ tên, đóng dấu)</span>
          <SoftBox mt={8}>
            <input disabled={containView} style={{ ...s.inputDotted, width: "150px", textAlign: "center" }} value={values.legalRepresentative} onChange={(e) => setFieldValue("legalRepresentative", e.target.value)} />
          </SoftBox>
        </div>
      </div> */}
    </SoftBox>
  );
}
